import * as React from "react";

import { ILeagueView, ILeagueRuleSet } from "../../models/interfaces";

export interface LeagueTableProps {
    leagueView: ILeagueView | null;
    leagueRuleSet?: ILeagueRuleSet | null;
    // number of league members flagged as winners (elimination leagues); only known on pages that load the members
    winners?: number;
};

const leagueDescription = (props: LeagueTableProps): JSX.Element | null => {
    if (props.leagueView && props.leagueView.description && props.leagueView.description.length > 0) {
        return <p>{props.leagueView.description}</p>;
    } else {
        return null;
    }
}

const membersMessage = (props: LeagueTableProps): JSX.Element | null => {
    if (!props.leagueView) {
        return null;
    }
    if (props.leagueView.members === 1) {
        return <span>There is <b>1</b> member.</span>;
    } else {
        return <span>There are <b>{props.leagueView.members.toString()}</b> members.</span>;
    }
}

const eliminatedMembersMessage = (props: LeagueTableProps): JSX.Element | null => {
    if (props.leagueRuleSet && !props.leagueRuleSet.elimination) {
        return null;
    }
    else if (props.leagueView && props.leagueView.eliminatedMembers === 0) {
        return <span>No one has been eliminated.</span>;
    } else {
        if (!props.leagueView) {
            return null;
        }

        const members = props.leagueView.members;
        let eliminatedMembers = props.leagueView.eliminatedMembers;
        let winners = 0;

        if (props.leagueView.complete) {
            // a winner can also carry the Eliminated flag (lost in the deciding week, won on the tiebreak),
            // so once the league is complete the winners come out of the eliminated count
            winners = props.winners !== undefined
                ? props.winners
                : Math.max(members - eliminatedMembers, 1);
            eliminatedMembers = Math.min(eliminatedMembers, members - winners);
        }

        const remainingMembers = members - eliminatedMembers - winners;

        let eliminated: JSX.Element;
        let remaining: JSX.Element | null = null;
        let won: JSX.Element | null = null;

        if (eliminatedMembers === 1) {
            eliminated = <span><b>1</b> member has been eliminated.</span>;
        } else {
            eliminated = <span><b>{eliminatedMembers.toString()}</b> members have been eliminated.</span>;
        }

        if (winners === 1) {
            won = <span><b>1</b> member won.</span>;
        } else if (winners > 1) {
            won = <span><b>{winners.toString()}</b> members won.</span>;
        }

        if (remainingMembers === 1) {
            remaining = <span><b>One</b> member remains.</span>;
        } else if (remainingMembers > 1 || winners === 0) {
            remaining = <span><b>{remainingMembers.toString()}</b> members are still playing.</span>;
        }

        return <span>{eliminated} {won} {remaining}</span>;
    }
}

const publicMessage = (props: LeagueTableProps) => {
    if (props.leagueView && props.leagueView.isPublic) {
        return "This league is public.";
    } else if (props.leagueView && !props.leagueView.isPublic && props.leagueView.isPublicViewable) {
        return "This league is not public, but it is public-viewable.";
    } else {
        return "This league is not public.";
    }
}

const LeagueTable = (props: LeagueTableProps): JSX.Element | null => {
    if (!props.leagueView || !props.leagueRuleSet) {
        return null;
    }
    return (
        <span>
            <p>
                <b>{props.leagueView.leagueName}</b> is a {props.leagueView.seasonName} <b>{props.leagueRuleSet.name}</b> league.
                The owner is <b>{props.leagueView.ownerName}</b>. {membersMessage(props)} {eliminatedMembersMessage(props)}
            </p>
            {leagueDescription(props)}
            <p>This league is{props.leagueView.locked ? '' : ' not'} locked.</p>
            <p>{publicMessage(props)}</p>
        </span>
    );
}

export default LeagueTable;
